// Opérations pures du constructeur de formulaire : ajout d'une section ou d'un champ, déplacement
// (dans la section ou vers une autre), suppression, génération d'un code unique à partir d'un
// libellé. Utilisées par le glisser-déposer comme par le clavier.
import { TYPES_CHAMP, type Champ, type Formulaire, type Section, type TypeChamp } from '@cc/contrats';

export interface PositionChamp {
  section: number;
  champ: number;
}

/** Code en minuscules, chiffres et soulignés, unique parmi les codes existants. */
export function genererCode(libelle: string, existants: Iterable<string>, prefixe = 'champ'): string {
  const base =
    libelle
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .replace(/^[^a-z]+/, '')
      .slice(0, 40) || prefixe;
  const pris = new Set(existants);
  if (!pris.has(base)) return base;
  let n = 2;
  while (pris.has(`${base}_${n}`)) n += 1;
  return `${base}_${n}`;
}

export function codesExistants(f: Formulaire): string[] {
  return f.sections.flatMap((s) => [s.code, ...s.champs.map((c) => c.code)]);
}

export function champParDefaut(type: TypeChamp, f: Formulaire): Champ {
  const libelle = `Nouveau champ ${f.sections.reduce((n, s) => n + s.champs.length, 0) + 1}`;
  const champ: Champ = { code: genererCode(libelle, codesExistants(f)), type, libelle, obligatoire: false };
  if (type === 'choix_unique' || type === 'choix_multiple') champ.options = [{ valeur: 'option_1', libelle: 'Option 1' }];
  if (type === 'tableau') champ.colonnes = [{ code: 'colonne_1', type: 'texte_court', libelle: 'Colonne 1', obligatoire: false }];
  return champ;
}

export function ajouterSection(f: Formulaire, titre = `Section ${f.sections.length + 1}`): Formulaire {
  const section: Section = { code: genererCode(titre, codesExistants(f), 'section'), titre, champs: [] };
  return { sections: [...f.sections, section] };
}

/** Ajoute un champ en fin de section (ou à l'index demandé) ; crée la première section si besoin. */
export function ajouterChamp(f: Formulaire, type: TypeChamp, section?: number, index?: number): { formulaire: Formulaire; position: PositionChamp } {
  const base = f.sections.length === 0 ? ajouterSection(f, 'Informations') : f;
  const s = Math.min(section ?? base.sections.length - 1, base.sections.length - 1);
  const champ = champParDefaut(type, base);
  const sections = base.sections.map((x, i) => {
    if (i !== s) return x;
    const champs = [...x.champs];
    const pos = index === undefined ? champs.length : Math.max(0, Math.min(index, champs.length));
    champs.splice(pos, 0, champ);
    return { ...x, champs };
  });
  const position = { section: s, champ: index === undefined ? (sections[s]?.champs.length ?? 1) - 1 : Math.max(0, Math.min(index, (sections[s]?.champs.length ?? 1) - 1)) };
  return { formulaire: { sections }, position };
}

export function supprimerChamp(f: Formulaire, p: PositionChamp): Formulaire {
  return { sections: f.sections.map((s, i) => (i === p.section ? { ...s, champs: s.champs.filter((_, j) => j !== p.champ) } : s)) };
}

export function supprimerSection(f: Formulaire, index: number): Formulaire {
  return { sections: f.sections.filter((_, i) => i !== index) };
}

export function remplacerChamp(f: Formulaire, p: PositionChamp, champ: Champ): Formulaire {
  return { sections: f.sections.map((s, i) => (i === p.section ? { ...s, champs: s.champs.map((c, j) => (j === p.champ ? champ : c)) } : s)) };
}

export function remplacerSection(f: Formulaire, index: number, section: Partial<Section>): Formulaire {
  return { sections: f.sections.map((s, i) => (i === index ? { ...s, ...section } : s)) };
}

/** Déplace un champ vers une position cible (section et index d'insertion, avant retrait). */
export function deplacerChamp(f: Formulaire, de: PositionChamp, vers: PositionChamp): { formulaire: Formulaire; position: PositionChamp } {
  const champ = f.sections[de.section]?.champs[de.champ];
  if (!champ) return { formulaire: f, position: de };
  const sections = f.sections.map((s) => ({ ...s, champs: [...s.champs] }));
  sections[de.section]?.champs.splice(de.champ, 1);
  const cible = sections[vers.section];
  if (!cible) return { formulaire: f, position: de };
  let index = vers.champ;
  if (vers.section === de.section && vers.champ > de.champ) index -= 1;
  index = Math.max(0, Math.min(index, cible.champs.length));
  cible.champs.splice(index, 0, champ);
  return { formulaire: { sections }, position: { section: vers.section, champ: index } };
}

/** Déplacement au clavier : d'un cran vers le haut ou le bas, en passant d'une section à l'autre aux bornes. */
export function deplacerChampDunCran(f: Formulaire, p: PositionChamp, sens: -1 | 1): { formulaire: Formulaire; position: PositionChamp } {
  const section = f.sections[p.section];
  if (!section) return { formulaire: f, position: p };
  const cible = p.champ + sens;
  if (cible >= 0 && cible < section.champs.length) {
    return deplacerChamp(f, p, { section: p.section, champ: sens > 0 ? cible + 1 : cible });
  }
  const autre = p.section + sens;
  if (autre < 0 || autre >= f.sections.length) return { formulaire: f, position: p };
  return deplacerChamp(f, p, { section: autre, champ: sens > 0 ? 0 : (f.sections[autre]?.champs.length ?? 0) });
}

export function deplacerSection(f: Formulaire, index: number, sens: -1 | 1): Formulaire {
  const j = index + sens;
  if (j < 0 || j >= f.sections.length) return f;
  const sections = [...f.sections];
  const a = sections[index] as Section;
  sections[index] = sections[j] as Section;
  sections[j] = a;
  return { sections };
}

/** Types proposés dans la palette, dans l'ordre de la maquette. */
export const TYPES_PALETTE: { type: TypeChamp; symbole: string }[] = [
  { type: 'texte_court', symbole: 'Aa' },
  { type: 'texte_long', symbole: '¶' },
  { type: 'nombre', symbole: '12' },
  { type: 'montant', symbole: 'F' },
  { type: 'date', symbole: 'JJ' },
  { type: 'choix_unique', symbole: '◉' },
  { type: 'choix_multiple', symbole: '☰' },
  { type: 'oui_non', symbole: 'O/N' },
  { type: 'telephone', symbole: 'Tél' },
  { type: 'courriel', symbole: '@' },
  { type: 'adresse', symbole: 'Adr' },
  { type: 'localisation', symbole: 'GPS' },
  { type: 'fichier', symbole: 'PDF' },
  { type: 'tableau', symbole: '▦' },
];

export function estTypeChamp(v: string): v is TypeChamp {
  return (TYPES_CHAMP as readonly string[]).includes(v);
}
